import { getLogger } from '@logtape/logtape';
import { randomId } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { uniqBy } from 'es-toolkit';
import { NavigateFunction } from 'react-router-dom';

import { AppRoute } from '@/constants/app-routes.ts';

import {
    CalendarPosition,
    ClickArea,
    TIMELINE_INTERVAL_CONFIG,
    TimelineInterval,
    TimelineIntervalConfig,
} from '@/models/Calendar.ts';
import { DayjsUnit } from '@/models/Dayjs.ts';
import { DailyAppointment, PlacedAppointment, SeriesPlacementConfig } from '@/models/appointment/Appointment.ts';
import {
    CalendarInfo,
    EnrichedPractitionerData,
    Practitioner,
    PractitionerCalendar,
} from '@/models/appointment/EnrichedPractitionerData.ts';
import { GetAppointmentsForTodayResponseDto } from '@/models/appointment/GetAppointmentsForTodayResponseDto.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { DataCurTimeScheme } from '@/models/data-cur/DataCurTimeScheme.ts';
import { DCCalendar, Timings } from '@/models/doc-cirrus/DCCalendar.ts';

import { closeAllModals } from '@/services/modalService.tsx';
import { showWarningNotification } from '@/services/notificationService.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';
import { useCalendarStore } from '@/stores/calendarStore.ts';
import { useFilterStore } from '@/stores/filterStore.ts';

import { getCalendarId } from '@/utils/appointmentUtils.ts';
import { getDateRangeForView } from '@/utils/date-range-utils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { openAppointmentScheduler } from '@/utils/modal/appointmentSchedulerUtils.tsx';

import { color_hash } from './colorUtils.ts';

const logger = getLogger(['util', 'calendar']);

export const MAX_APPOINTMENT_WIDTH = 98; // 98% to leave small gaps between columns
export const HOVER_HEIGHT_EXPANSION_PERCENTAGE = 10;
export const BASE_HEADER_HEIGHT = 50;
export const WHOLE_DAY_APPOINTMENT_HEIGHT = 20;
export const CALENDAR_COLUMNS = 5;

export const getIntervalConfig = (interval: TimelineInterval): TimelineIntervalConfig => {
    return TIMELINE_INTERVAL_CONFIG[interval] ?? { click_segments: 3, label: 'Default', minutes: 30 };
};

export const getClickAreas = (click_segments: number, timeline_interval: number): ClickArea[] => {
    if (click_segments === 1) {
        return [{ offset: 0, height: '100%' }];
    } else if (click_segments === 2) {
        const half = timeline_interval / 2;
        return [
            { offset: 0, height: '50%' },
            { offset: Math.round(half), height: '50%' },
        ];
    } else {
        // 3 segments
        const third = timeline_interval / 3;
        return [
            { offset: 0, height: '33.33%' },
            { offset: Math.round(third), height: '33.33%' },
            { offset: Math.round(third * 2), height: '33.34%' },
        ];
    }
};

export const isFullDayAppointment = (appointment: DailyAppointment): boolean => {
    const start_time = dayjs(appointment.start);
    const end_time = dayjs(appointment.end);
    const duration_hours = end_time.diff(start_time, 'hour');

    // Consider appointments of exactly 24 hours as full day appointments
    return duration_hours === 24;
};

export const NUMBER_OF_DAYS_IN_WORK_WEEK = 5;
export const NUMBER_OF_CALENDAR_COLUMNS = 5;

export enum CalendarColumnType {
    DAY = 'day',
    WEEK = 'week',
}

// Generates time slots based on the specified interval in minutes
export const generateTimeSlots = (interval_minutes: number = 30): { time: string }[] => {
    const slots_per_hour = 60 / interval_minutes;
    const total_slots = 24 * slots_per_hour;

    return Array.from({ length: total_slots }).map((_, index) => {
        const total_minutes = index * interval_minutes;
        const hours = Math.floor(total_minutes / 60);
        const minutes = total_minutes % 60;

        const hour_string = hours.toString().padStart(2, '0');
        const minute_string = minutes.toString().padStart(2, '0');

        return { time: `${hour_string}:${minute_string}` };
    });
};

export const calculateAppointmentHeight = (
    duration_minutes: number,
    container_height: number,
    timeslot_height: number,
    timeline_interval: number = 30
): number => {
    const pixels_per_minute = timeslot_height / timeline_interval;
    const min_height = 10;
    const duration = Math.max(duration_minutes, min_height / pixels_per_minute);
    const height_in_pixels = duration * pixels_per_minute;
    return (height_in_pixels / container_height) * 100;
};

export const calculateAppointmentTop = (
    start_time: Date | string,
    container_height: number,
    time_slot_height: number,
    timeline_interval: number = 30
): number => {
    const time = dayjs(start_time);
    const minutes_since_start = time.hour() * 60 + time.minute();
    const pixels_per_minute = time_slot_height / timeline_interval;
    const position = minutes_since_start * pixels_per_minute;
    return (position / container_height) * 100;
};

export const detectAppointmentOverlap = (appointment_1: DailyAppointment, appointment_2: DailyAppointment): boolean => {
    // Only consider minutes, ignore seconds for overlap detection
    const start1 = dayjs(appointment_1.start).startOf('minute');
    const end1 = dayjs(appointment_1.end).startOf('minute');
    const start2 = dayjs(appointment_2.start).startOf('minute');
    const end2 = dayjs(appointment_2.end).startOf('minute');

    // Appointments overlap only if there's actual time overlap
    // Touching appointments (one ends when another starts) do NOT overlap
    return start1.isBefore(end2) && start2.isBefore(end1);
};

// Returns the later of app.end and app.start + duration, rounded to minute precision.
// Rounding matches the original overlap detection behavior — appointments that merely
// touch at the same minute (A ends 09:30, B starts 09:30) are NOT considered overlapping.
const getEffectiveEnd = (app: DailyAppointment): number => {
    const end_time = dayjs(app.end).startOf('minute').valueOf();
    const duration_end = dayjs(app.start).startOf('minute').add(app.duration.value, 'minute').valueOf();
    return Math.max(end_time, duration_end);
};

const getStartMinute = (app: DailyAppointment): number => dayjs(app.start).startOf('minute').valueOf();

/**
 * Groups overlapping appointments using a sweep-line approach (O(N log N)).
 * Appointments are grouped transitively — if A overlaps B and B overlaps C,
 * all three are in the same group even if A doesn't directly overlap C.
 */
export const groupOverlappingAppointments = (appointments: DailyAppointment[]): Record<string, DailyAppointment[]> => {
    if (appointments.length === 0) return {};

    // Filter out full-day appointments — they render in a separate row
    const filtered_appointments = appointments.filter((app) => !isFullDayAppointment(app));
    if (filtered_appointments.length === 0) return {};

    // Sort by start time for the sweep-line to work correctly
    const sorted_appointments = [...filtered_appointments].sort((a, b) => {
        const start_diff = dayjs(a.start).diff(dayjs(b.start));
        if (start_diff !== 0) return start_diff;
        return a.id.localeCompare(b.id);
    });

    // Sweep through sorted appointments: if the next appointment starts before
    // the current group's latest end time, it belongs in the same group
    const groups: DailyAppointment[][] = [];
    let current_group: DailyAppointment[] = [sorted_appointments[0]];
    let group_end_time = getEffectiveEnd(sorted_appointments[0]);

    for (let i = 1; i < sorted_appointments.length; i++) {
        const appointment = sorted_appointments[i];
        const start_time = getStartMinute(appointment);

        if (start_time < group_end_time) {
            // Overlaps with current group — extend the group's end time
            current_group.push(appointment);
            group_end_time = Math.max(group_end_time, getEffectiveEnd(appointment));
        } else {
            // No overlap — finalize current group and start a new one
            groups.push(current_group);
            current_group = [appointment];
            group_end_time = getEffectiveEnd(appointment);
        }
    }

    // Don't forget the last group
    groups.push(current_group);

    // Convert to keyed record format expected by callers
    const result: Record<string, DailyAppointment[]> = {};
    for (const [group_index, group] of groups.entries()) {
        result[`group${group_index}`] = group;
    }

    return result;
};

/**
 * Computes positions for all appointments in a group.
 * Greedy column assignment — each appointment gets equal width within the group.
 */
export const calculateGroupPositions = (group_appointments: DailyAppointment[]): Map<string, CalendarPosition> => {
    const default_position = { left: 0, width: MAX_APPOINTMENT_WIDTH, column_position: 0 };

    // Single or no appointments get full width — no layout needed
    if (group_appointments.length <= 1) {
        const result = new Map<string, CalendarPosition>();
        if (group_appointments.length === 1) {
            result.set(group_appointments[0].id, default_position);
        }
        return result;
    }

    // Sort by start time for stable, deterministic column assignment
    const sorted_appointments = [...group_appointments].sort((a, b) => {
        const start_diff = dayjs(a.start).diff(dayjs(b.start));
        if (start_diff !== 0) return start_diff;
        return a.id.localeCompare(b.id);
    });

    // Greedy column assignment — place each appointment in the first
    // column where it doesn't overlap with the column's last appointment
    const column_assignments = new Map<string, number>();
    const column_end_times = new Map<number, number>();

    for (const app of sorted_appointments) {
        const start_time = getStartMinute(app);
        let assigned_column = 0;

        // Find the first column where this appointment fits
        while (column_end_times.has(assigned_column)) {
            const column_end = column_end_times.get(assigned_column)!;
            if (start_time >= column_end) break;
            assigned_column += 1;
        }

        column_assignments.set(app.id, assigned_column);
        column_end_times.set(assigned_column, getEffectiveEnd(app));
    }

    const total_columns = Math.max(...column_assignments.values()) + 1;
    const column_width = MAX_APPOINTMENT_WIDTH / total_columns;

    const positions = new Map<string, CalendarPosition>();
    for (const app of sorted_appointments) {
        const appointment_column = column_assignments.get(app.id) ?? 0;
        positions.set(app.id, {
            column_position: appointment_column,
            left: appointment_column * column_width,
            width: column_width,
        });
    }

    return positions;
};

export const groupAppointmentsByCalendar = (
    appointments: DailyAppointment[] | undefined
): Record<string, DailyAppointment[]> => {
    const appointments_by_calendar: Record<string, DailyAppointment[]> = {};

    if (!appointments) {
        return appointments_by_calendar;
    }

    for (const appointment of appointments) {
        const calendar_id = getCalendarId(appointment);

        if (!calendar_id) {
            logger.warn('No calendar id found for appointment', { appointment: appointment });
            continue;
        }

        // Initialize array if this is the first appointment for this calendar
        if (!appointments_by_calendar[calendar_id]) {
            appointments_by_calendar[calendar_id] = [];
        }
        appointments_by_calendar[calendar_id].push(appointment);
    }

    return appointments_by_calendar;
};

// Resolves ids directly rather than via groupAppointmentsByCalendar, which logs the whole
// appointment (patient data) when an id is missing. This runs on every render of the filter.
export const getBookedCalendarIds = (appointments: DailyAppointment[] | undefined): Set<string> => {
    const bookedCalendarIds = new Set<string>();

    if (!appointments) {
        return bookedCalendarIds;
    }

    for (const appointment of appointments) {
        const calendarId = getCalendarId(appointment);

        if (calendarId) {
            bookedCalendarIds.add(calendarId);
        }
    }

    return bookedCalendarIds;
};

const handleAppointmentLimit = (appointments: DailyAppointment[]): DailyAppointment[] => {
    // REMOVED APPOINTMENT LIMIT FOR TESTING - just sort and return all appointments
    const prepared_appointments: DailyAppointment[] = appointments.sort((a, b) => a.start.localeCompare(b.start));
    return prepared_appointments;
};

export const groupAppointmentsByDate = (
    appointments: DailyAppointment[] | undefined,
    selected_calendars: string[]
): Record<string, DailyAppointment[]> => {
    const appointments_by_date: Record<string, DailyAppointment[]> = {};

    if (!appointments) {
        return appointments_by_date;
    }

    const selected_calendars_set = new Set(selected_calendars);

    const filtered_appointments: DailyAppointment[] = appointments.filter((apt) => {
        const calendar_id = getCalendarId(apt);
        return calendar_id && selected_calendars_set.has(calendar_id);
    });

    const prepared_appointments = handleAppointmentLimit(filtered_appointments);

    for (const appointment of prepared_appointments) {
        // Use local time for date assignment to handle timezone differences properly
        const date_key = dayjs(appointment.start).local().format('YYYY-MM-DD');

        if (!appointments_by_date[date_key]) {
            appointments_by_date[date_key] = [];
        }

        appointments_by_date[date_key].push(appointment);
    }

    return appointments_by_date;
};

export const extractCalendarInfo = (practitioners: Practitioner[] | undefined): [CalendarInfo[], CalendarInfo[]] => {
    if (!practitioners) {
        return [[], []];
    }

    const doc_cirrus_calendar_info: CalendarInfo[] = [];
    const data_cur_calendar_info: CalendarInfo[] = [];

    const dc_practice_names: Map<string, string> = new Map<string, string>();

    for (const practitioner of practitioners) {
        if (practitioner.pvs_provider !== PVSProvider.DOC_CIRRUS) {
            continue;
        }

        for (const practice of practitioner.practices) {
            if (!practice.pvs) continue;

            dc_practice_names.set(practice.pvs.id, practice.pvs.name);
        }
    }

    for (const practitioner of practitioners) {
        if (practitioner.pvs_provider === PVSProvider.DOC_CIRRUS) {
            // Handle DOC_CIRRUS practitioner
            const doc_cirrus_calendars: CalendarInfo[] = practitioner.calendars.map(
                (calendar: PractitionerCalendar) => ({
                    id: calendar.pvs.id,
                    name: practitioner.calendars.length === 1 ? practitioner.name : calendar.pvs.name,
                    timings: (calendar.pvs as DCCalendar).timings,
                    location_id: (calendar.pvs as DCCalendar).location_id,
                    location_name: dc_practice_names.get((calendar.pvs as DCCalendar).location_id),
                    practitioner_name: practitioner.name,
                })
            );
            doc_cirrus_calendar_info.push(...doc_cirrus_calendars);
        } else if (practitioner.pvs_provider === PVSProvider.DATA_CUR) {
            // Handle DATA_CUR practitioner
            const data_cur_calendars: CalendarInfo[] = practitioner.calendars.map((calendar: PractitionerCalendar) => ({
                id: calendar.pvs.id,
                name: practitioner.calendars.length === 1 ? practitioner.name : calendar.pvs.name,
                // Get timings from the practitioner's calendar_timings_map (AZ availability)
                timings: practitioner.calendar_timings_map?.get(calendar.pvs.id),
                location_id: undefined, // DataCur doesn't have location_id concept
            }));
            data_cur_calendar_info.push(...data_cur_calendars);
        }
        // Add more PVS providers here as needed (e.g., TIGRAIS)
    }

    return [doc_cirrus_calendar_info, data_cur_calendar_info];
};

/**
 * Convert dayjs format (0=Sunday, 1=Monday...) to Monday-start format (1=Monday, 2=Tuesday...)
 */
const dateToMondayStartOfWeek = (dayjs_day: number): number => {
    return dayjs_day === 0 ? 7 : dayjs_day;
};

export interface TimeSegment {
    start_minute: number; // Minutes from slot start (0-29)
    end_minute: number; // Minutes from slot start (0-29)
    color: string;
    is_available: boolean;
    timing_id: string;
}

export const getTimeSlotInfo = (
    time_str: string, // in format "HH:mm"
    timings: Timings | undefined,
    selected_date: Date | undefined,
    practitioner?: Practitioner,
    timeline_interval: number = 30
): { is_available: boolean; segments: TimeSegment[] } => {
    // First check if the slot is in OOO period
    const is_ooo = isTimeSlotInOOO(time_str, selected_date, practitioner, timeline_interval);

    if (is_ooo) {
        return { is_available: false, segments: [] };
    }

    if (!timings) return { is_available: true, segments: [] }; // If no timings data, assume available

    // Parse the time slot string (format: "HH:mm")
    const [hours, minutes] = time_str.split(':').map(Number);

    // Get day of week using dayjs, converted to Monday-start format
    let day_of_week: number;
    if (selected_date) {
        day_of_week = dateToMondayStartOfWeek(dayjs(selected_date).day());
    } else {
        day_of_week = dateToMondayStartOfWeek(dayjs().day());
    }

    if (!timings?.base) {
        return { is_available: false, segments: [] };
    }

    // Calculate slot boundaries based on timeline interval
    const slot_start_minutes = hours * 60 + minutes;
    const slot_end_minutes = slot_start_minutes + timeline_interval;

    const segments: TimeSegment[] = [];
    let has_any_availability = false;

    // Check base timings (regular schedule)
    for (const timing of timings.base) {
        if (!timing.days_of_week.includes(day_of_week)) continue;

        const [start_hour, start_minute] = timing.start_time;
        const [end_hour, end_minute] = timing.end_time;

        const timing_start_minutes = start_hour * 60 + start_minute;
        const timing_end_minutes = end_hour * 60 + end_minute;

        // Check if timing overlaps with this slot
        const overlap_start = Math.max(slot_start_minutes, timing_start_minutes);
        const overlap_end = Math.min(slot_end_minutes, timing_end_minutes);

        if (overlap_start < overlap_end) {
            // There's an overlap, create a segment
            const segment: TimeSegment = {
                start_minute: overlap_start - slot_start_minutes,
                end_minute: overlap_end - slot_start_minutes,
                color: timing.color,
                is_available: true,
                timing_id: timing.id,
            };
            segments.push(segment);
            has_any_availability = true;
        }
    }

    // Sort segments by start time and merge overlapping ones with same color
    segments.sort((a, b) => a.start_minute - b.start_minute);

    return { is_available: has_any_availability, segments: segments };
};

export const isTimeSlotAvailable = (
    time_str: string,
    timings: Timings | undefined,
    selected_date: Date | undefined,
    practitioner?: Practitioner,
    timeline_interval: number = 30
): boolean => {
    return getTimeSlotInfo(time_str, timings, selected_date, practitioner, timeline_interval).is_available;
};

export const isTimeSlotInOOO = (
    time_str: string,
    selected_date: Date | undefined,
    practitioner: Practitioner | undefined,
    timeline_interval: number = 30
): boolean => {
    if (!practitioner || !selected_date) return false;

    const [hours, minutes] = time_str.split(':').map(Number);
    const slot_start = dayjs(selected_date).hour(hours).minute(minutes).second(0).millisecond(0);
    const slot_end = slot_start.add(timeline_interval, 'minute'); // Dynamic slot duration

    if (practitioner.pvs_provider === PVSProvider.DOC_CIRRUS) {
        return isDocCircusTimeSlotInOOO(slot_start, slot_end, practitioner);
    }

    if (practitioner.pvs_provider === PVSProvider.DATA_CUR) {
        return isDataCurTimeSlotInOOO(slot_start, slot_end, selected_date, practitioner);
    }

    return false;
};

/**
 * Checks DocCircus OOO schedules for conflicts
 */
const isDocCircusTimeSlotInOOO = (
    slot_start: dayjs.Dayjs,
    slot_end: dayjs.Dayjs,
    practitioner: Practitioner
): boolean => {
    // Check OOO schedules (holidays, vacations, etc.)
    if (practitioner.ooo_schedules) {
        for (const ooo of practitioner.ooo_schedules) {
            const ooo_start = dayjs(ooo.start);
            const ooo_end = dayjs(ooo.end);

            // Check if the slot overlaps with OOO period
            if (slot_start.isBefore(ooo_end) && slot_end.isAfter(ooo_start)) {
                return true;
            }
        }
    }

    return false;
};

// Datacur has a bug where schemes of type URLAUB and KRANK and others (from the `terminplaner/urlaubstage` has an end date as a day, and actual api end date one day after)
const isSchemeOneDayBugged = (scheme: DataCurTimeScheme): boolean => {
    return scheme.category?.toUpperCase().includes('URLAUB') || scheme.category?.toUpperCase().includes('KRANK');
};

/**
 * Checks DataCur OOO time schemes
 */
const isDataCurTimeSlotInOOO = (
    slot_start: dayjs.Dayjs,
    slot_end: dayjs.Dayjs,
    selected_date: Date,
    practitioner: Practitioner
): boolean => {
    const date_str = dayjs(selected_date).format('YYYY-MM-DD');
    const raw_day = dayjs(selected_date).format('dd').toUpperCase();

    // Map German day abbreviations to English (DataCur uses English abbreviations)
    const day_mapping: Record<string, string> = {
        MO: 'MO', // Monday
        DI: 'TU', // Dienstag -> Tuesday
        MI: 'WE', // Mittwoch -> Wednesday
        DO: 'TH', // Donnerstag -> Thursday
        FR: 'FR', // Friday
        SA: 'SA', // Saturday
        SO: 'SU', // Sonntag -> Sunday
    };

    const day_of_week = day_mapping[raw_day] || raw_day;
    const slot_start_minutes = slot_start.hour() * 60 + slot_start.minute();
    const slot_end_minutes = slot_end.hour() * 60 + slot_end.minute();

    // Combine all time schemes and check for blocking periods
    // AZ = Available time (working hours) - don't block
    // ST = Special time (holidays, blocked periods) - block slots
    // LOC = Location-based blocked time - block slots
    const all_time_schemes = [...(practitioner.ooo_time_schemes || []), ...(practitioner.time_schemes || [])];

    for (const scheme of all_time_schemes) {
        // Skip AZ type schedules (these are available time, not blocking)
        if (scheme.type === 'AZ') continue;

        const actual_end_date = isSchemeOneDayBugged(scheme)
            ? dayjs(scheme.end_date).subtract(1, 'day')
            : scheme.end_date;

        // Check if date is within the scheme's date range (if specified)
        if (scheme.start_date && actual_end_date) {
            const scheme_start_date = dayjs(scheme.start_date);
            const scheme_end_date = dayjs(actual_end_date);
            const current_date = dayjs(date_str);

            if (!current_date.isBetween(scheme_start_date, scheme_end_date, 'day', '[]')) {
                continue;
            }
        }

        // Simple and bulletproof day matching
        if (scheme.week_days && scheme.week_days.toUpperCase().includes(day_of_week)) {
            // Check time overlap
            const [start_h, start_m] = scheme.start_time.split(':').map(Number);
            const [end_h, end_m] = scheme.end_time.split(':').map(Number);

            const scheme_start_minutes = start_h * 60 + start_m;
            const scheme_end_minutes = end_h * 60 + end_m;

            // Handle edge case where end time is 23:59 (treat as full day)
            const effective_end_minutes = scheme_end_minutes === 23 * 60 + 59 ? 24 * 60 : scheme_end_minutes;

            if (slot_start_minutes < effective_end_minutes && slot_end_minutes > scheme_start_minutes) {
                return true;
            }
        }
    }

    return false;
};

export const generateDateRange = (start_date: Date | undefined, end_date: Date | undefined): Date[] => {
    if (!start_date || !end_date) return [];

    const start = dayjs(start_date);
    const days_count = dayjs(end_date).diff(start, DayjsUnit.DAY) + 1;

    return Array.from({ length: days_count }, (_, index) => start.add(index, DayjsUnit.DAY).toDate());
};

export const navigateToToday = (): void => {
    const today = dayjs().toDate();
    const { is_week_view_enabled: isWeekViewEnabled, is_weekend_visible: isWeekendVisible } =
        useCalendarStore.getState();
    const [startDate, endDate] = getDateRangeForView(today, isWeekViewEnabled, isWeekendVisible);

    useFilterStore.setState({
        start_date: startDate,
        end_date: endDate,
    });
};

export const setCalendarWeekView = (isWeekViewEnabled: boolean): void => {
    useCalendarStore.setState({ is_week_view_enabled: isWeekViewEnabled });

    const { is_weekend_visible: isWeekendVisible } = useCalendarStore.getState();
    const anchor = isWeekViewEnabled ? (useFilterStore.getState().start_date ?? dayjs().toDate()) : dayjs().toDate();
    const [startDate, endDate] = getDateRangeForView(anchor, isWeekViewEnabled, isWeekendVisible);

    useFilterStore.setState({
        start_date: startDate,
        end_date: endDate,
    });
};

export const setCalendarWeekendVisible = (isWeekendVisible: boolean): void => {
    const { is_week_view_enabled: isWeekViewEnabled } = useCalendarStore.getState();
    useCalendarStore.setState({ is_weekend_visible: isWeekendVisible });

    if (!isWeekViewEnabled) return;

    const anchor = useFilterStore.getState().start_date ?? dayjs().toDate();
    const [startDate, endDate] = getDateRangeForView(anchor, true, isWeekendVisible);

    useFilterStore.setState({
        start_date: startDate,
        end_date: endDate,
    });
};

// The usage of this function should be memoized.
export const calendarColor = (appointment: DailyAppointment): string => {
    const calendar_id = getCalendarId(appointment);
    if (!calendar_id) return '#e5d8d8';

    return color_hash.hex(calendar_id);
};

export const filterAppointmentsByDateRange = (
    appointments: GetAppointmentsForTodayResponseDto,
    start_date_string: string,
    end_date_string: string
): GetAppointmentsForTodayResponseDto => {
    const filtered_appointments = { ...appointments };

    filtered_appointments.data.active = filtered_appointments.data.active.filter((appointment) => {
        const appointment_date = dayjs(appointment.start).local().format('YYYY-MM-DD');
        return dayjs(appointment_date).isBetween(start_date_string, end_date_string, undefined, '[]');
    });

    filtered_appointments.data.inactive = filtered_appointments.data.inactive.filter((appointment) => {
        const appointment_date = dayjs(appointment.start).local().format('YYYY-MM-DD');
        return dayjs(appointment_date).isBetween(start_date_string, end_date_string, undefined, '[]');
    });

    return filtered_appointments;
};

export const getCalendarForAppointment = (
    column_type: CalendarColumnType,
    selected_calendar_ids: string[],
    current_day_calendar?: Partial<DCCalendar>,
    all_calendars?: CalendarInfo[]
): [string, string] => {
    if (column_type === CalendarColumnType.DAY) {
        return [current_day_calendar?.id ?? '', current_day_calendar?.location_id ?? ''];
    }

    if (selected_calendar_ids.length > 1) {
        return ['', ''];
    }

    const selected_calendar_id = selected_calendar_ids[0];
    const selected_calendar = all_calendars?.find((cal) => cal.id === selected_calendar_id);

    return [selected_calendar_id, selected_calendar?.location_id ?? ''];
};

export const groupAppointmentsByDateAndOverlaps = (
    appointments_by_date: Record<string, DailyAppointment[]>
): Record<string, Record<string, DailyAppointment[]>> => {
    const result: Record<string, Record<string, DailyAppointment[]>> = {};

    // For each date in the appointments_by_date object
    for (const [date, appointments] of Object.entries(appointments_by_date)) {
        result[date] = groupOverlappingAppointments(appointments);
    }

    return result;
};

export const calculateMaxAllDayAppointments = (
    appointments: DailyAppointment[],
    start_date: Date,
    end_date: Date,
    is_week_view_enabled: boolean,
    selected_calendars: string[],
    filtered_calendars?: CalendarInfo[],
    appointments_by_calendar?: Record<string, DailyAppointment[]>
): number => {
    if (!appointments) return 0;

    // Function to count full day appointments in a group
    // For day view, we also check if the appointment is on the selected date
    const countAllDayAppointments = (apps: DailyAppointment[], checkDate?: boolean): number => {
        return apps.filter((app) => {
            if (app.links?.calendar_id && !selected_calendars.includes(app.links.calendar_id)) return false;

            // Check if it's a full day appointment
            const is_full_day_appt = isFullDayAppointment(app);

            // If we need to check date (for day view), verify it's on the selected date
            if (checkDate && is_full_day_appt) {
                const app_date = dayjs(app.start).format('YYYY-MM-DD');
                const selected_date_str = dayjs(start_date).format('YYYY-MM-DD');
                return app_date === selected_date_str;
            }

            return is_full_day_appt;
        }).length;
    };

    let max_count = 0;

    if (is_week_view_enabled) {
        // For week view, check each date
        let appointments_by_date = groupAppointmentsByDate(appointments, selected_calendars);

        // Filter out appointments with keys not between start and end date
        const filtered_appointments_by_date: Record<string, DailyAppointment[]> = {};
        const start_date_key = dayjs(start_date).format('YYYY-MM-DD');
        const end_date_key = dayjs(end_date).format('YYYY-MM-DD');

        for (const [date_key, date_appointments] of Object.entries(appointments_by_date)) {
            if (date_key >= start_date_key && date_key <= end_date_key) {
                filtered_appointments_by_date[date_key] = date_appointments;
            }
        }

        appointments_by_date = filtered_appointments_by_date;

        for (const apps of Object.values(appointments_by_date)) {
            const invalid_count = countAllDayAppointments(apps);
            max_count = Math.max(max_count, invalid_count);
        }
    } else if (filtered_calendars && appointments_by_calendar) {
        // For day view, use the same filtered calendars that we're rendering
        // This ensures consistency between what's displayed and what's calculated
        for (const calendar of filtered_calendars) {
            // Get appointments for this calendar
            const apps = appointments_by_calendar[calendar.id] || [];
            if (apps.length === 0) continue;

            // Count invalid appointments for the selected day
            const invalid_count = countAllDayAppointments(apps, true);
            max_count = Math.max(max_count, invalid_count);
        }
    }

    return max_count;
};

export const calculateContainerHeight = (timeline_interval: TimelineInterval): number => {
    const total_slots = (24 * 60) / timeline_interval;
    const original_slot_height = (window.innerHeight * 2.5) / 48;
    const max_slot_height = original_slot_height * 2;

    const slot_heights: Record<TimelineInterval, number> = {
        1: max_slot_height * 0.15,
        5: max_slot_height * 0.2,
        10: max_slot_height * 0.25,
        15: max_slot_height * 0.3,
        20: max_slot_height * 0.35,
        30: max_slot_height * 0.4,
        60: max_slot_height * 0.45,
    };

    const slot_height = slot_heights[timeline_interval] || max_slot_height * 0.7;
    return slot_height * total_slots;
};

export const sortPractitionerCalendars = (practitioners: EnrichedPractitionerData[]): CalendarInfo[] => {
    const [doc_cirrus_calendars, data_cur_calendars] = extractCalendarInfo(
        practitioners.flatMap((d) => d.practitioners)
    );

    // 1. group calendars by practice
    const calendars_by_practice: Map<string, CalendarInfo[]> = new Map();
    for (const calendar of doc_cirrus_calendars) {
        const location_name = calendar.location_name ?? '';
        const entry = calendars_by_practice.get(location_name);

        if (entry) {
            entry.push(calendar);
        } else {
            calendars_by_practice.set(location_name, [calendar]);
        }
    }

    // 2. for each practice, sort calendars by "doctor name - calendar name"
    const names = [...calendars_by_practice.keys()];
    for (const name of names) {
        const sorted_calendars = calendars_by_practice.get(name)?.sort((a, b) => {
            const a_name = `${a.practitioner_name} - ${a.name}`;
            const b_name = `${b.practitioner_name} - ${b.name}`;
            return a_name.localeCompare(b_name);
        });
        calendars_by_practice.set(name, sorted_calendars ?? []);
    }

    // 3. sort practices by name
    const sorted_practices = [...calendars_by_practice.keys()].sort((a, b) => a.localeCompare(b));
    const sorted_calendars_by_practice = sorted_practices.map((practice) => calendars_by_practice.get(practice) ?? []);
    const flat_doc_cirrus_calendars = sorted_calendars_by_practice.flat();
    const sorted_data_cur_calendars = data_cur_calendars.sort((a, b) => a.name.localeCompare(b.name));

    const merged_calendars = [...flat_doc_cirrus_calendars, ...sorted_data_cur_calendars];
    return uniqBy(merged_calendars, (calendar) => calendar.id);
};

// ─── CALENDAR SERIES SPECIFIC ────────────────────────────────────────────────

interface EnterPlacementParams {
    placementConfig: SeriesPlacementConfig;
    locationKey: string;
    navigate: NavigateFunction;
    initialPlacements?: PlacedAppointment[];
}

export const enterSeriesPlacementMode = ({
    placementConfig,
    locationKey,
    navigate,
    initialPlacements,
}: EnterPlacementParams): void => {
    // Set placement state BEFORE closing modals.
    // resetAllData() (triggered by modal onClose) will preserve these when isSeriesPlacementMode is true.
    useBookingStore.setState({
        isSeriesPlacementMode: true,
        seriesPlacementConfig: placementConfig,
        seriesPlacedAppointments: initialPlacements ? sortAndNumberPlacements(initialPlacements) : [],
    });

    closeAllModals();

    // Filter calendar to only show the practitioner's calendar
    useFilterStore.setState({
        selected_calendars: {
            ...useFilterStore.getState().selected_calendars,
            [locationKey]: [placementConfig.practitionerCalendarId],
        },
    });

    useCalendarStore.setState({ is_sidebar_open: true });
    navigate(AppRoute.CALENDAR_VIEW);
};

export const exitSeriesPlacementMode = (locationKey: string): void => {
    const bookingState = useBookingStore.getState();

    // Double-click guard — already exited
    if (!bookingState.isSeriesPlacementMode) return;

    const previousSelection = bookingState.seriesPlacementConfig?.previousCalendarSelection;

    // Restore previous calendar selection
    if (previousSelection) {
        useFilterStore.setState({
            selected_calendars: {
                ...useFilterStore.getState().selected_calendars,
                [locationKey]: previousSelection,
            },
        });
    }

    // Capture series state before clearing placement
    const seriesState = {
        isSeriesMode: bookingState.isSeriesMode,
        collectionStep: bookingState.collectionStep,
        collection_configuration: bookingState.collection_configuration,
        collection_occurrences: bookingState.collection_occurrences,
        collectionPastAppointments: bookingState.collectionPastAppointments,
    };
    const patient = bookingState.current_patient;

    // Reopen scheduler at the series list step BEFORE clearing placement flag
    // so the modal blocks interaction and prevents slot clicks from opening walk-in modal
    if (patient) {
        openAppointmentScheduler({ mode: 'book', patient: patient });
        useBookingStore.getState().reopenInSeriesMode(patient, seriesState);
    }

    // Clear placement state after scheduler is open
    useBookingStore.setState({
        isSeriesPlacementMode: false,
        seriesPlacementConfig: undefined,
        seriesPlacedAppointments: [],
    });
};

export const confirmExitSeriesPlacement = (locationKey: string, placedAppointmentsCount: number): void => {
    if (placedAppointmentsCount === 0) {
        exitSeriesPlacementMode(locationKey);
        return;
    }

    modals.openConfirmModal({
        title: 'Platzierung abbrechen?',
        centered: true,
        children: 'Nicht gespeicherte Termine gehen verloren. Möchten Sie die Platzierung wirklich abbrechen?',
        confirmProps: {
            color: 'red',
            className: 'text-white hover:text-white',
        },
        labels: { confirm: 'Abbrechen', cancel: 'Weiter platzieren' },
        onConfirm: () => exitSeriesPlacementMode(locationKey),
    });
};

export const sortAndNumberPlacements = (placements: PlacedAppointment[]): PlacedAppointment[] =>
    [...placements]
        .sort((a, b) => dayjs(a.startDatetime).valueOf() - dayjs(b.startDatetime).valueOf())
        .map((a, i) => ({ ...a, position: i + 1 }));

interface PlaceSeriesAppointmentParams {
    columnDate: Date | undefined;
    slotTime: ReturnType<typeof dayjs>;
    calendarId?: string;
}

export const placeSeriesAppointment = ({ columnDate, slotTime, calendarId }: PlaceSeriesAppointmentParams): void => {
    const { seriesPlacementConfig, seriesPlacedAppointments } = useBookingStore.getState();
    if (!seriesPlacementConfig || !columnDate) return;

    if (seriesPlacedAppointments.length >= seriesPlacementConfig.totalAppointments) {
        showWarningNotification('Alle Termine wurden bereits platziert');
        return;
    }

    const placementDate = dayjs(columnDate)
        .startOf(DayjsUnit.DAY)
        .hour(slotTime.hour())
        .minute(slotTime.minute())
        .startOf(DayjsUnit.MINUTE);

    if (placementDate.isBefore(dayjs())) {
        showWarningNotification('Termine können nicht in der Vergangenheit platziert werden');
        return;
    }

    const endDate = placementDate.add(seriesPlacementConfig.durationMinutes, 'minute');
    const startIso = placementDate.toISOString();
    const endIso = endDate.toISOString();
    const newStart = placementDate.valueOf();
    const newEnd = endDate.valueOf();

    // Check for exact duplicate
    const isDuplicate = seriesPlacedAppointments.some((a) => a.startDatetime === startIso);
    if (isDuplicate) {
        showWarningNotification('Dieser Zeitslot ist bereits belegt');
        return;
    }

    // Check for time overlap with existing placements
    const hasOverlap = seriesPlacedAppointments.some((a) => {
        const existingStart = dayjs(a.startDatetime).valueOf();
        const existingEnd = dayjs(a.endDatetime).valueOf();
        return newStart < existingEnd && existingStart < newEnd;
    });
    if (hasOverlap) {
        showWarningNotification('Dieser Termin überschneidet sich mit einem bereits platzierten Termin');
        return;
    }

    const newPlacement: PlacedAppointment = {
        id: randomId(),
        position: 0,
        startDatetime: startIso,
        endDatetime: endIso,
        calendarId: calendarId,
    };

    const sorted = sortAndNumberPlacements([...seriesPlacedAppointments, newPlacement]);
    useBookingStore.setState({ seriesPlacedAppointments: sorted });
};

export const removeSeriesPlacement = (position: number): void => {
    const current = useBookingStore.getState().seriesPlacedAppointments;
    const target = current.find((a) => a.position === position);
    if (target?.isPast) return;

    const renumbered = sortAndNumberPlacements(current.filter((a) => a.position !== position));
    useBookingStore.setState({ seriesPlacedAppointments: renumbered });
};
